import { Component, Inject, Input } from '@angular/core';
import { fadeInOut } from '@shared/src/animations';
import { DeliveryLineColumns } from '@shared/src/components/route/delivery/lines/DeliveryLineColumns';
import { IDeliveryLine } from '@shared/src/components/route/delivery/lines/IDeliveryLine';
import { HomeController } from '@shared/src/controllers/home/home.controller';
import { OpenRouteCMRController } from '@shared/src/controllers/open/openroutecmr.controller';
import { RouteCMRController } from '@shared/src/controllers/route/cmr/routecmr.controller';
import { HLong } from '@shared/src/datatypes/HLong';
import { RouteShipmentDeliveryNoteDto } from '@shared/src/dtos/route/cmr/RouteShipmentDeliveryNoteDto';

/***
 * Mostra els albarans (un per shipment) d'un routePartCMR com a document
 * digital, al costat del CMR (veure shared.route.part.cmr.digitaldocument.component).
 * Sempre visible, sense cap control d'accés (a diferència del CMR).
 *
 * Per cada shipment, el back ja indica (RouteShipmentDeliveryNoteDto.editable)
 * si l'usuari actual n'és el propietari: en aquest cas s'incrusta l'editor
 * complet ja existent (shared-shipment-delivery-component, amb lots inclosos)
 * en comptes de repintar-ne un altre; la resta es mostren només en lectura
 * amb shared-delivery-line.
 */
@Component({
  selector: 'shared-route-part-shipmentdeliverynote-digitaldocument-component',
  templateUrl: './shared.route.part.shipmentdeliverynote.digitaldocument.component.html',
  styleUrls: ['./shared.route.part.shipmentdeliverynote.digitaldocument.component.scss'],
  animations: [fadeInOut]
})
export class SharedRoutePartShipmentDeliveryNoteDigitalDocumentComponent {

  constructor(private routeCMRController: RouteCMRController,
    private openRouteCMRController: OpenRouteCMRController,
    @Inject('HomeController') protected homeController: HomeController) {
    if (homeController.useIonic) this.useIonic = homeController.useIonic();
  }

  public useIonic: boolean = false;

  @Input() public readonly: boolean = false;
  @Input() public external: boolean = false;

  private _routePartCMRId: number;
  @Input()
  public set routePartCMRId(value: number) {
    if (this._routePartCMRId === value)
      return;
    this._routePartCMRId = value;
    this.loadDeliveryNotes();
  }
  public get routePartCMRId(): number {
    return this._routePartCMRId;
  }

  public deliveryNotes: RouteShipmentDeliveryNoteDto[] = [];

  /***
   * Camps que aquesta vista sap pintar (els mateixos que ja edita
   * shared-shipment-delivery-component). Un camp que existeixi a
   * IDeliveryLine però no sigui en aquesta llista queda amagat SEMPRE,
   * sense error (veure el comentari gran a DeliveryLineColumns.ts).
   */
  private static readonly SUPPORTEDCOLUMNS: string[] = ['reference', 'description', 'quantity', 'units', 'kg',
    'netKg', 'boxes', 'bags', 'sacks', 'litres', 'wDimension', 'yDimension', 'hDimension', 'm3', 'palletCount',
    'palletType', 'unitPrice', 'totalPrice', 'packageId', 'userTags', 'publicTags', 'privateTags', 'batchInfo'];

  public allColumns: DeliveryLineColumns = DeliveryLineColumns.compute([], true,
    SharedRoutePartShipmentDeliveryNoteDigitalDocumentComponent.SUPPORTEDCOLUMNS);

  /*** Columnes visibles per a cada albarà (només lectura): una entrada per note, mateix índex que deliveryNotes */
  public columnsByNote: DeliveryLineColumns[] = [];

  loadDeliveryNotes() {
    if (HLong.isNullOrNullLong(this._routePartCMRId)) {
      this.welcomeDeliveryNotes([]);
      return;
    }
    let obs = !this.external
      ? this.routeCMRController.getRouteDeliveryNotes(this._routePartCMRId)
      : this.openRouteCMRController.getRouteDeliveryNotes(this._routePartCMRId);
    obs.subscribe(data => {
      this.welcomeDeliveryNotes(data != null && data.deliveryNotes != null ? data.deliveryNotes : []);
    });
  }

  private welcomeDeliveryNotes(deliveryNotes: RouteShipmentDeliveryNoteDto[]) {
    this.deliveryNotes = deliveryNotes;
    this.columnsByNote = this.deliveryNotes.map(note => {
      let lines: IDeliveryLine[] = note.delivery != null ? note.delivery.lines : [];
      return DeliveryLineColumns.compute(lines, false,
        SharedRoutePartShipmentDeliveryNoteDigitalDocumentComponent.SUPPORTEDCOLUMNS);
    });
    this.expandedLine = null;
  }

  /*** Quina línia (només lectura) està desplegada: com la vista del CMR, no manté estat per línia. */
  public expandedLine: any = null;

  public onExpandedLine(line: any) {
    this.expandedLine = this.expandedLine === line ? null : line;
  }

  public isLineExpanded(line: any): boolean {
    return this.expandedLine === line;
  }
}
